import type { WorldCapabilities } from "@uzverse/contracts";
import type { CameraMode } from "@uzverse/core";
import { quatToYaw } from "@uzverse/world-scene";
import type { SceneNode } from "@uzverse/world-scene";
import { useSyncExternalStore } from "react";
import { fetchCapabilities } from "../api.js";
import { isLandmark } from "../bounds.js";
import { buildingBox, doorOf } from "../generators.js";
import {
  canEnter,
  floorSurfaceY,
  roomLayout,
  yawIntoRoom,
  yawOutOfRoom,
} from "../interior/layout.js";
import { loadQuality, saveQuality, initialAutoTier, storedQuality } from "../perf/quality.js";
import { layoutStore, storedLayout } from "../layout.js";
import { pushWorldPref, syncWorldPrefs } from "../prefs.js";
import type { WorldPrefs } from "../prefs.js";
import type { QualitySetting, RenderLevel } from "../perf/quality.js";
import { adoptTimeSetting, cachedTimeSetting } from "../look/timeStore.js";
import { adoptWeatherSetting, cachedWeatherSetting } from "../look/weatherStore.js";
import { worldStore } from "../store.js";
import type { WorldStore } from "../store.js";
import type { ToastSpec } from "../blueprint/controller.js";
import { availableModes, isWalkMode, loadMode, saveMode, storedMode } from "./modes.js";
import { elevators } from "../elevator/store.js";
import { newWalker } from "./walk.js";
import type { FlyState, WalkerState } from "./walk.js";

export interface InsideState {
  id: string;
  level: number;
}

/** A request for the orbit camera to fly somewhere (consumed by the framing rig). */
export interface FlightRequest {
  seq: number;
  target: [number, number, number];
  position: [number, number, number];
}

export interface ToastItem extends ToastSpec {
  id: number;
}

export interface NavState {
  mode: CameraMode;
  /** The building the player is inside of (interior cutaway), with the floor they stand on. */
  inside: InsideState | null;
  /**
   * Outside cutaway: one floor of a building opened up from the orbit camera (no need to go in).
   * Exclusive with `inside`.
   */
  cutaway: InsideState | null;
  /** 0 clear .. 1 black: the fade between outside and inside. */
  fade: number;
  caps: WorldCapabilities | null;
  quality: QualitySetting;
  /** The adaptive tier (auto mode), driven by the performance monitor. */
  autoTier: RenderLevel;
  perf: boolean;
  /** The building whose door the player stands in front of. */
  nearDoor: string | null;
  toasts: ToastItem[];
  /** Pointer lock is on (desktop look). Opt-in, never started by a plain click. */
  locked: boolean;
}

export const FADE_MS = 220;
const TOAST_MS = 5000;
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Rotates a local (x, z) by the building's yaw and adds its position: local frame to world. */
export function localToWorld(
  origin: { x: number; z: number; yaw: number },
  x: number,
  z: number,
): [number, number] {
  const c = Math.cos(origin.yaw);
  const s = Math.sin(origin.yaw);
  return [origin.x + x * c + z * s, origin.z - x * s + z * c];
}

/** The world pose of a root node's frame. */
export function frameOf(node: SceneNode): { x: number; z: number; yaw: number } {
  return {
    x: node.transform.position[0],
    z: node.transform.position[2],
    yaw: quatToYaw(node.transform.rotation) ?? 0,
  };
}

/**
 * Navigation state shared by the canvas rig, the HUD and the keyboard: the camera mode, the
 * building the player is inside of, fades, toasts, quality and the capabilities of the user. The
 * walker and fly poses are plain mutable fields (read every frame, never rendered through React).
 */
export class NavStore {
  private state: NavState;
  private listeners = new Set<() => void>();
  private nextToast = 1;
  /** Position of the player: world frame outside, the building's local frame inside. */
  walker: WalkerState = newWalker(0, 40, 0);
  fly: FlyState = { x: 0, y: 60, z: 40, yaw: 0, pitch: -0.4 };
  /** Where the orbit camera should look after a walk or flight (consumed by the rig). */
  orbitReturn: [number, number, number] | null = null;
  /** The orbit camera's last pose, so walking can start from where the user was looking. */
  orbitPose: { position: [number, number, number]; target: [number, number, number] } | null = null;
  /** The orbit camera was framed on real data at least once (so later mounts restore, not reframe). */
  orbitFramed = false;
  /** Bumped whenever the walker was teleported (entering, floors), so the rig re-syncs. */
  teleports = 0;
  /** The orbit camera should fly here (the rig applies it and remembers the seq it has seen). */
  flight: FlightRequest | null = null;
  /** Set by the live rig: asks the browser for pointer lock (opt-in mouse look). */
  lockHandler: (() => void) | null = null;
  private flightSeq = 0;
  private busy = false;

  constructor(private readonly world: WorldStore = worldStore) {
    this.state = {
      mode: loadMode(),
      inside: null,
      cutaway: null,
      fade: 0,
      caps: null,
      quality: loadQuality(),
      autoTier: initialAutoTier(),
      perf: false,
      nearDoor: null,
      toasts: [],
      locked: false,
    };
  }

  getState = (): NavState => this.state;

  subscribe = (l: () => void): (() => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };

  private set(patch: Partial<NavState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  // ------------------------------------------------------------------ capabilities

  async loadCapabilities(): Promise<void> {
    try {
      const caps = await fetchCapabilities();
      this.set({ caps });
      if (!availableModes(caps.cameraModes).includes(this.state.mode)) this.setMode("orbit");
    } catch {
      // Without capabilities the user simply gets no drag handles; every mode still works.
      this.set({ caps: null });
    }
  }

  modes(): CameraMode[] {
    return availableModes(this.state.caps?.cameraModes);
  }

  // ------------------------------------------------------------------ modes

  /** Switches the camera mode (keys 1..5 and the buttons). Leaving a building exits it. */
  setMode(mode: CameraMode): void {
    if (mode === this.state.mode || !this.modes().includes(mode)) return;
    const was = this.state.mode;
    if (this.state.cutaway) this.set({ cutaway: null });
    if (this.state.inside) {
      if (isWalkMode(mode)) {
        this.remember(mode);
        this.set({ mode });
        return;
      }
      void this.exit().then(() => this.finishSwitch(was, mode));
      return;
    }
    this.finishSwitch(was, mode);
  }

  private finishSwitch(was: CameraMode, mode: CameraMode): void {
    if (was === "orbit" || was === "blueprint") this.captureOrbitSpawn();
    if (isWalkMode(mode) && !isWalkMode(was)) this.spawnWalker(was);
    if (mode === "fly" && was !== "fly") this.spawnFly(was);
    if (!isWalkMode(mode) && mode !== "fly") {
      // Back to the map view: look at where the player was.
      if (isWalkMode(was) || was === "fly") {
        const at = was === "fly" ? this.fly : this.walker;
        this.orbitReturn = [at.x, 0, at.z];
      }
    }
    this.remember(mode);
    this.set({ mode, nearDoor: null });
  }

  /** Remembers what the orbit camera looks at (the rig reports it when it can). */
  private captureOrbitSpawn(): void {
    const sel = this.world.getState().selected;
    const node = sel ? this.world.node(sel.id) : undefined;
    if (node && node.parentId === null && node.geometry.kind === "procedural") {
      const f = frameOf(node);
      const door = doorOf(node.geometry.params);
      const [wx, wz] = localToWorld(
        f,
        door.position[0] + door.normal[0] * 5,
        door.position[1] + door.normal[1] * 5,
      );
      // Stand 5 m in front of the door, facing the building.
      this.walker = newWalker(wx, wz, f.yaw + yawIntoRoom(door));
      this.teleports += 1;
      this.spawnedAtSelection = true;
      return;
    }
    this.spawnedAtSelection = false;
  }
  private spawnedAtSelection = false;

  private spawnWalker(from: CameraMode): void {
    if (this.spawnedAtSelection && (from === "orbit" || from === "blueprint")) return;
    if (from === "fly") {
      this.walker = newWalker(this.fly.x, this.fly.z, this.fly.yaw);
    } else if (this.orbitPose) {
      const { position: p, target: t } = this.orbitPose;
      const yaw = Math.atan2(-(t[0] - p[0]), -(t[2] - p[2]));
      this.walker = newWalker(t[0], t[2], yaw);
    }
    this.teleports += 1;
  }

  private spawnFly(from: CameraMode): void {
    if (isWalkMode(from)) {
      const w = this.walker;
      this.fly = { x: w.x, y: w.y + 25, z: w.z, yaw: w.yaw, pitch: -0.5 };
    } else if (this.orbitPose) {
      const { position: p, target: t } = this.orbitPose;
      this.fly = {
        x: p[0],
        y: Math.max(8, p[1]),
        z: p[2],
        yaw: Math.atan2(-(t[0] - p[0]), -(t[2] - p[2])),
        pitch: -0.45,
      };
    }
    this.teleports += 1;
  }

  // ------------------------------------------------------------------ buildings

  /** May the user enter this building now? */
  entryOf(id: string): { ok: true } | { ok: false; reason: "members" | "none" } {
    const node = this.world.node(id);
    if (!node || node.parentId !== null) return { ok: false, reason: "none" };
    const org = this.world.getState().org;
    const building = org?.companies.flatMap((c) => c.buildings).find((b) => b.id === id) ?? null;
    return canEnter(node, building, isLandmark(node))
      ? { ok: true }
      : { ok: false, reason: node.geometry.kind === "procedural" ? "members" : "none" };
  }

  /** Walks through the door: fade out, switch to the interior of the ground floor, fade in. */
  async enter(id: string): Promise<boolean> {
    const node = this.world.node(id);
    const entry = this.entryOf(id);
    if (!node || node.geometry.kind !== "procedural" || this.busy) return false;
    if (!entry.ok) {
      this.toast({
        kind: "info",
        key: entry.reason === "members" ? "nav.membersOnly" : "nav.cannotEnter",
      });
      return false;
    }
    this.busy = true;
    try {
      await this.fadeTo(1);
      const room = roomLayout(node.geometry.params);
      const [ix, iz] = room.door.inside;
      this.walker = newWalker(ix, iz, yawIntoRoom(room.door), floorSurfaceY(0));
      this.teleports += 1;
      const mode: CameraMode = isWalkMode(this.state.mode) ? this.state.mode : "first_person";
      this.remember(mode);
      this.set({ inside: { id, level: 0 }, cutaway: null, mode, nearDoor: null });
      this.world.select(null);
      await sleep(60);
      await this.fadeTo(0);
    } finally {
      this.busy = false;
    }
    return true;
  }

  /** Back out through the door (always possible, from any floor). */
  async exit(): Promise<void> {
    const inside = this.state.inside;
    if (!inside || this.busy) return;
    const node = this.world.node(inside.id);
    this.busy = true;
    try {
      await this.fadeTo(1);
      if (node && node.geometry.kind === "procedural") {
        const door = doorOf(node.geometry.params);
        const f = frameOf(node);
        const [wx, wz] = localToWorld(
          f,
          door.position[0] + door.normal[0] * 2.4,
          door.position[1] + door.normal[1] * 2.4,
        );
        this.walker = newWalker(wx, wz, f.yaw + yawOutOfRoom(door));
        this.teleports += 1;
      }
      this.set({ inside: null });
      await sleep(60);
      await this.fadeTo(0);
    } finally {
      this.busy = false;
    }
  }

  /**
   * The elevator. Inside a building with a running elevator it is the real ride: if the player is
   * not in a cabin one is docked at this floor and the player is placed in it (a short fade), the
   * floor is pressed on its panel, the doors close, the cabin rides and the doors open at the
   * destination. Without one (no interior mounted) it is the old short fade to the same spot.
   */
  async goToFloor(level: number): Promise<void> {
    const inside = this.state.inside;
    if (!inside || inside.level === level) return;
    const lift = elevators.get(inside.id);
    const to = lift ? lift.indexOfLevel(level) : -1;
    if (!lift || to < 0 || this.busy) {
      await this.teleportToFloor(level);
      return;
    }
    let cabin = lift.cabinOfPlayer();
    if (cabin < 0 || lift.sim.cabins[cabin]!.phase === "moving") {
      this.busy = true;
      try {
        await this.fadeTo(1, FADE_MS / 2);
        cabin = lift.board(
          Math.max(0, lift.levels.indexOf(this.state.inside?.level ?? inside.level)),
        );
        const pose = lift.boardPose(cabin);
        this.walker = {
          ...this.walker,
          x: pose.x,
          z: pose.z,
          yaw: pose.yaw,
          pitch: 0,
          y: floorSurfaceY(this.state.inside?.level ?? inside.level),
          vy: 0,
          onGround: true,
        };
        lift.inCab[cabin] = true;
        this.teleports += 1;
        await this.fadeTo(0, FADE_MS / 2);
      } finally {
        this.busy = false;
      }
    }
    lift.pressFloor(cabin, to);
    await lift.arrived(cabin, to);
  }

  /** Puts the walker at a height (a ride ended inside one frame): the rig re-syncs to it. */
  carryTo(y: number): void {
    this.walker = { ...this.walker, y, vy: 0, onGround: true };
  }

  /** Changes the drawn floor without moving the player (the rider passes a midpoint). */
  swapLevel(level: number): void {
    const inside = this.state.inside;
    if (inside && inside.level !== level) this.set({ inside: { ...inside, level } });
  }

  /** Fade, same spot on another floor (no elevator running). */
  private async teleportToFloor(level: number): Promise<void> {
    const inside = this.state.inside;
    if (!inside || inside.level === level) return;
    await this.fadeTo(1, FADE_MS / 2);
    this.walker = {
      ...this.walker,
      y: floorSurfaceY(level),
      vy: 0,
      onGround: true,
    };
    this.teleports += 1;
    this.set({ inside: { ...inside, level } });
    await this.fadeTo(0, FADE_MS / 2);
  }

  /** Steps the elevator (or the cutaway) one floor up or down within the building's floors. */
  async stepFloor(delta: 1 | -1, levels: readonly number[]): Promise<void> {
    const cur = this.state.inside?.level ?? this.state.cutaway?.level;
    if (cur === undefined || levels.length === 0) return;
    const sorted = [...levels].sort((a, b) => a - b);
    const i = sorted.indexOf(cur);
    const next = sorted[Math.max(0, Math.min(sorted.length - 1, (i < 0 ? 0 : i) + delta))]!;
    if (next === cur) return;
    if (this.state.inside) await this.goToFloor(next);
    else if (this.state.cutaway) this.showFloor(this.state.cutaway.id, next);
  }

  /**
   * Outside cutaway: peels the building down to one floor and frames it from the orbit camera.
   * Members only, like going inside.
   */
  showFloor(id: string, level: number): boolean {
    const node = this.world.node(id);
    if (!node || node.geometry.kind !== "procedural" || this.state.inside) return false;
    const entry = this.entryOf(id);
    if (!entry.ok) {
      this.toast({
        kind: "info",
        key: entry.reason === "members" ? "nav.membersOnly" : "nav.cannotEnter",
      });
      return false;
    }
    const room = roomLayout(node.geometry.params);
    const f = frameOf(node);
    const y = floorSurfaceY(level);
    const span = Math.max(room.width, room.depth);
    // From the door side, high enough to look down into the floor.
    const [fx, fz] = localToWorld(f, 0, span);
    const len = Math.hypot(fx - f.x, fz - f.z) || 1;
    const k = (span * 1.35) / len;
    this.requestFlight(
      [f.x, y, f.z],
      [f.x + (fx - f.x) * k, y + span * 1.25, f.z + (fz - f.z) * k],
    );
    this.set({ cutaway: { id, level } });
    return true;
  }

  closeCutaway(): void {
    if (this.state.cutaway) this.set({ cutaway: null });
  }

  /** Flies the orbit camera to a building, keeping the viewing direction the user has. */
  focusBuilding(id: string): void {
    const node = this.world.node(id);
    if (!node || node.parentId !== null || node.geometry.kind !== "procedural") return;
    const box = buildingBox(node.geometry.params, { windows: false });
    const f = frameOf(node);
    const span = Math.max(box.width, box.depth, 14);
    const ty = Math.min(box.height / 2, 30);
    let dx = 0.45;
    let dy = 0.6;
    let dz = 0.65;
    if (this.orbitPose) {
      const { position: p, target: t } = this.orbitPose;
      dx = p[0] - t[0];
      dy = Math.max(p[1] - t[1], 4);
      dz = p[2] - t[2];
    }
    const len = Math.hypot(dx, dy, dz) || 1;
    const dist = span * 1.8 + box.height * 0.5;
    this.requestFlight(
      [f.x, ty, f.z],
      [f.x + (dx / len) * dist, ty + (dy / len) * dist, f.z + (dz / len) * dist],
    );
  }

  requestFlight(target: [number, number, number], position: [number, number, number]): void {
    this.flightSeq += 1;
    this.flight = { seq: this.flightSeq, target, position };
    for (const l of this.listeners) l();
  }

  /** Opt-in mouse look (pointer lock). Esc gives the mouse back. */
  requestLock(): void {
    this.lockHandler?.();
  }

  private async fadeTo(value: 0 | 1, ms: number = FADE_MS): Promise<void> {
    this.set({ fade: value });
    await sleep(ms + 20);
  }

  setNearDoor(id: string | null): void {
    if (id !== this.state.nearDoor) this.set({ nearDoor: id });
  }

  setLocked(locked: boolean): void {
    if (locked !== this.state.locked) this.set({ locked });
  }

  // ------------------------------------------------------------------ toasts, quality, perf

  toast(spec: ToastSpec): void {
    const item: ToastItem = { ...spec, id: this.nextToast++ };
    this.set({ toasts: [...this.state.toasts.slice(-2), item] });
    setTimeout(() => this.dismiss(item.id), TOAST_MS);
  }

  dismiss(id: number): void {
    if (this.state.toasts.some((t) => t.id === id)) {
      this.set({ toasts: this.state.toasts.filter((t) => t.id !== id) });
    }
  }

  setQuality(quality: QualitySetting): void {
    saveQuality(quality);
    pushWorldPref({ quality });
    this.set({ quality });
  }

  /** Cache the mode on this device and save it as the user's setting (follows them across devices). */
  private remember(mode: CameraMode): void {
    saveMode(mode);
    pushWorldPref({ cameraMode: mode });
  }

  /**
   * Applies the user's saved camera mode and quality tier (the server wins over this device's
   * cache; values only this device has are saved to the server). Called once the world opens.
   * Returns the merged prefs so the dock can take its open state from the same read.
   */
  async loadPrefs(local: WorldPrefs = {}): Promise<WorldPrefs> {
    const cameraMode = storedMode();
    const quality = storedQuality();
    const layout = storedLayout();
    const timeOfDay = cachedTimeSetting();
    const weather = cachedWeatherSetting();
    const merged = await syncWorldPrefs({
      ...(weather !== undefined ? { weather } : {}),
      ...(cameraMode ? { cameraMode } : {}),
      ...(quality ? { quality } : {}),
      ...(layout ? { layout } : {}),
      ...(timeOfDay !== undefined ? { timeOfDay } : {}),
      ...local,
    });
    if (merged.quality && merged.quality !== this.state.quality) {
      saveQuality(merged.quality);
      this.set({ quality: merged.quality });
    }
    if (merged.cameraMode && merged.cameraMode !== this.state.mode) {
      this.setMode(merged.cameraMode);
    }
    layoutStore.adopt(merged.layout);
    adoptTimeSetting(merged.timeOfDay);
    adoptWeatherSetting(merged.weather);
    return merged;
  }

  setAutoTier(tier: RenderLevel): void {
    if (tier !== this.state.autoTier) this.set({ autoTier: tier });
  }

  togglePerf(): void {
    this.set({ perf: !this.state.perf });
  }
}

export const navStore = new NavStore();

export function useNavState(nav: NavStore = navStore): NavState {
  return useSyncExternalStore(nav.subscribe, nav.getState);
}

/** A slice of the navigation state; the component re-renders only when the slice changes. */
export function useNavSelector<T>(select: (state: NavState) => T, nav: NavStore = navStore): T {
  return useSyncExternalStore(nav.subscribe, () => select(nav.getState()));
}
